import { Input, LazyLoader, Select, SelectOptionType, SliderModal } from '@bp/ui-components';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import {
  TimeGridEntryMappingInput,
  useTimeGridMigrationCardsQuery,
  useTimeGridQuery,
} from '../../../types/planung-graphql-client-defs';
import { TimeGridMigrationMapping } from '../../Timetable/TimeGridMigration/TimeGridMigrationMapping';
import { useTimeGridMigration } from '../../Timetable/TimeGridMigration/useTimeGridMigration';
import styles from './CopyVersionToNewTimetableModal.module.scss';
import { NewTimetableFormType, newTimetableSchema } from './validation/schema';

export type CopyVersionToNewTimetableValues = {
  comment: string;
  /** Nur gesetzt, wenn das neue Zeitraster vom Raster der kopierten Version abweicht. */
  migration: { mapping: TimeGridEntryMappingInput[]; resetAvailabilities: boolean } | null;
  name: string;
  timeGridUuid: string;
};

type CopyVersionToNewTimetableModalProps = {
  existingTimetableNames: string[];
  isOpen: boolean;
  sourceComment: string;
  sourceTimeGridUuid: string | null;
  sourceTimetableName: string;
  /** Version, die kopiert wird – `null`, solange das Modal geschlossen ist. */
  version: { uuid: string; version: number } | null;
  onClose: () => void;
  onSubmit: (values: CopyVersionToNewTimetableValues) => Promise<boolean>;
};

/**
 * „Kopieren nach → Neuer Stundenplan“: erst die Stammdaten des neuen Plans, und nur wenn dabei ein
 * anderes Zeitraster gewählt wird, als zweiter Schritt die Zuordnung der Stunden.
 */
export const CopyVersionToNewTimetableModal = ({
  existingTimetableNames,
  isOpen,
  sourceComment,
  sourceTimeGridUuid,
  sourceTimetableName,
  version,
  onClose,
  onSubmit,
}: CopyVersionToNewTimetableModalProps) => {
  const { t } = useTranslation();
  const schoolYear = useSelectedSchoolYear();

  const [values, setValues] = useState<NewTimetableFormType>({ name: '', comment: '', timeGridUuid: '' });
  const [nameTouched, setNameTouched] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  //biome-ignore lint/correctness/useExhaustiveDependencies(t): suppress dependency t
  useEffect(() => {
    if (!isOpen) return;
    setValues({
      name: t('timetableVersion.copyDefaultName', { name: sourceTimetableName }),
      comment: sourceComment,
      timeGridUuid: sourceTimeGridUuid ?? '',
    });
    setNameTouched(false);
    setSlideIndex(0);
  }, [isOpen, sourceComment, sourceTimeGridUuid, sourceTimetableName]);

  const [{ data: timeGridData, fetching: fetchingTimeGrids }] = useTimeGridQuery({ pause: !isOpen });
  const [{ data: cardsData }] = useTimeGridMigrationCardsQuery({
    variables: { versionUuid: version?.uuid ?? '' },
    pause: !isOpen || !version,
    requestPolicy: 'network-only',
  });

  const timeGrids = timeGridData?.timeGrids ?? [];
  const oldTimeGrid = timeGrids.find((timeGrid) => timeGrid.uuid === sourceTimeGridUuid);
  const needsMigration = !!sourceTimeGridUuid && !!values.timeGridUuid && values.timeGridUuid !== sourceTimeGridUuid;
  const newTimeGrid = needsMigration ? timeGrids.find((timeGrid) => timeGrid.uuid === values.timeGridUuid) : undefined;

  const cards = useMemo(
    () =>
      cardsData?.placedTimetableCards.map((card) => ({
        uuid: card.uuid,
        startEntryUuid: card.startTimeGridEntryUuid ?? null,
        endEntryUuid: card.endTimeGridEntryUuid ?? null,
      })) ?? [],
    [cardsData],
  );

  const migration = useTimeGridMigration({ cards, newTimeGrid, oldTimeGrid });

  const validation = newTimetableSchema(existingTimetableNames).safeParse(values);
  const nameError = validation.error?.issues.find((issue) => issue.path[0] === 'name')?.message;

  const timeGridOptions: SelectOptionType[] = timeGrids.map((timeGrid) => ({
    value: timeGrid.uuid,
    label: timeGrid.name,
  }));

  const handleSubmit = async () => {
    if (!validation.success) return;
    setIsSubmitting(true);
    const succeeded = await onSubmit({
      name: validation.data.name,
      comment: validation.data.comment,
      timeGridUuid: validation.data.timeGridUuid,
      migration: needsMigration
        ? { mapping: migration.mappingInput, resetAvailabilities: migration.resetAvailabilities }
        : null,
    });
    setIsSubmitting(false);
    if (succeeded) onClose();
  };

  const formSlide = (
    <div className={styles.step} key='form'>
      <div className={styles['step-content']}>
        <p className={styles.source}>
          {t('timetableVersion.copySource', { name: sourceTimetableName, version: version?.version ?? '' })}
        </p>
        {fetchingTimeGrids ? (
          <LazyLoader embedded />
        ) : (
          <div className={styles.form}>
            <Input
              label={t('common.name')}
              name='name'
              value={values.name}
              onChange={(e) => setValues((prev) => ({ ...prev, name: e.target.value }))}
              onBlur={() => setNameTouched(true)}
              // Der vorbelegte Name kann schon vergeben sein – das soll sofort sichtbar sein, nicht erst nach dem Verlassen.
              error={nameTouched || values.name.trim() !== '' ? nameError : undefined}
            />
            <Input label={t('common.schoolYear')} name='schoolYear' value={schoolYear?.name ?? ''} readonly />
            <Input
              className={styles.full}
              label={t('common.comment')}
              name='comment'
              value={values.comment}
              onChange={(e) => setValues((prev) => ({ ...prev, comment: e.target.value }))}
            />
            <Select
              className={styles.full}
              label={t('timeGrid.title', { count: 1 })}
              name='timeGridUuid'
              // Der Slider schneidet Überstehendes ab – das Menü muss darüber liegen.
              menuPosition='fixed'
              options={timeGridOptions}
              value={timeGridOptions.find((option) => option.value === values.timeGridUuid) ?? null}
              onChange={(option) =>
                setValues((prev) => ({
                  ...prev,
                  timeGridUuid: ((option as SingleValue<SelectOptionType>)?.value as string) ?? '',
                }))
              }
            />
          </div>
        )}
      </div>
    </div>
  );

  const migrationSlide = (
    <div className={styles.step} key='migration'>
      <div className={styles['step-content']}>
        <TimeGridMigrationMapping
          migration={migration}
          newTimeGridName={newTimeGrid?.name ?? null}
          oldTimeGridName={oldTimeGrid?.name ?? null}
          resetAvailabilitiesLabel={t('timetableVersion.copyResetAvailabilities')}
        />
      </div>
    </div>
  );

  return (
    <SliderModal
      // Das Zeitraster entscheidet, ob es einen zweiten Schritt gibt: gleiches Raster → direkt „Speichern“,
      // anderes Raster → erst „Weiter“ zur Stunden-Zuordnung, gespeichert wird auf der letzten Seite.
      slides={needsMigration ? [formSlide, migrationSlide] : [formSlide]}
      startIndex={slideIndex}
      isOpen={isOpen}
      title={t('timetableVersion.copyToNewTimetableTitle')}
      closeText={t('common.cancel')}
      previousText={t('common.back')}
      nextText={t('common.next')}
      submitText={t('common.save')}
      nextDisabled={!validation.success || fetchingTimeGrids}
      submitDisabled={!validation.success || isSubmitting || (needsMigration && (slideIndex === 0 || !newTimeGrid))}
      loading={isSubmitting}
      closeOnSubmit={false}
      modalSettings={{ width: 'm' }}
      onChange={setSlideIndex}
      onClose={onClose}
      onSubmit={() => void handleSubmit()}
    />
  );
};
